import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MaterialImports } from '../../material.imports';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../../services/toast.service';
import { NotificationService } from '../../services/notification.service';
import { LoggerService } from '../../core/logger.service';
import { LanguageService, SupportedLanguage } from '../../services/language.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login-layout',
  standalone: true,
  imports: [CommonModule, ...MaterialImports],
  templateUrl: './login-layout.html',
  styleUrl: './login-layout.scss',
})
export class LoginLayout implements OnInit {
  loginForm: FormGroup;
  token: string = 'test';
  loginError: string = '';
  showLoginError: boolean = false;
  showPassword: boolean = false;
  isLoading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private toastService: ToastService,
    private notificationService: NotificationService,
    private logger: LoggerService,
    private languageService: LanguageService,
    private translate: TranslateService
  ) {
    this.loginForm = new FormGroup({
      username: new FormControl('', Validators.required),
      password: new FormControl('', Validators.required),
    });
  }

  ngOnInit(): void {
    // Login first-paints in Lao to match BOF (user decision 2026-06-18).
    // Display-only (translate.use, no persist) — does not overwrite the user's
    // saved app-wide preference; the ລາວ/EN pill still switches & persists.
    this.translate.use('lo');

    // Check for token in URL query params
    this.route.queryParams.subscribe(p => {
      this.logger.log('[Login] Checking query params for token');
      this.token = p['token'];

      // Case 1: Token provided in URL (coming from BOF)
      if (this.token) {
        this.logger.log('[Login] Token found in query params, storing in localStorage');
        localStorage.setItem('token', this.token);

        // Validate the new token
        const decoded = this.authService.decodeToken() as any;
        this.logger.log('[Login] Decoded token claims:', { UserId: decoded?.UserId, UserCode: decoded?.UserCode, UserTypeId: decoded?.UserTypeId, exp: decoded?.exp });

        if (this.isTokenValid(decoded)) {
          this.logger.log('[Login] Token is valid, initializing SignalR and redirecting to home');
          // Initialize SignalR for real-time notifications
          if (decoded?.UserCode && decoded?.UserTypeId) {
            this.notificationService.initializeSignalR(decoded.UserCode, decoded.UserTypeId);
            this.logger.log('[Login] SignalR initialized for user:', decoded.UserCode);
          } else if (decoded?.UserId && decoded?.UserTypeId) {
            this.notificationService.initializeSignalR(decoded.UserId.toString(), decoded.UserTypeId);
            this.logger.warn('[Login] UserCode not found, using UserId:', decoded.UserId);
          }
          this.router.navigate(['/agent-hub']);
        } else {
          this.logger.error('[Login] Token from URL is invalid or expired');
          localStorage.removeItem('token');
          // Stay on login page, don't redirect to /bye
        }
      }
      // Case 2: No token in URL, check if already logged in (existing session)
      else if (this.authService.isLoggedIn()) {
        const decoded = this.authService.decodeToken() as any;
        this.logger.log('[Login] Existing token found, validating...', { UserId: decoded?.UserId, UserCode: decoded?.UserCode });

        if (this.isTokenValid(decoded)) {
          this.logger.log('[Login] Existing token is valid, redirecting to home');
          // Re-initialize SignalR in case connection was lost
          if (decoded?.UserCode && decoded?.UserTypeId) {
            this.notificationService.initializeSignalR(decoded.UserCode, decoded.UserTypeId);
          }
          this.router.navigate(['/agent-hub']);
        } else {
          this.logger.warn('[Login] Existing token is expired, clearing...');
          localStorage.removeItem('token');
          // Stay on login page
        }
      }
      // Case 3: No token anywhere - just show login form
      else {
        this.logger.log('[Login] No token found, showing login form');
        // Do nothing - user will see the login form
      }
    });
  }

  private isTokenValid(decoded: any): boolean {
    if (!decoded) {
      this.logger.warn('[Token Validation] Token is null or undefined');
      return false;
    }
    if (!decoded.exp) {
      this.logger.warn('[Token Validation] No exp claim found in token');
      return false;
    }
    const now = Math.floor(Date.now() / 1000);
    const isValid = decoded.exp > now;
    if (!isValid) {
      this.logger.warn('[Token Validation] Token expired. exp:', decoded.exp, 'now:', now);
    }
    return isValid;
  }

  // Language pill — reuses the app-wide LanguageService (ngx-translate, reactive, no reload).
  get isLao(): boolean {
    return this.languageService.getCurrentLanguage() !== 'en';
  }

  switchLang(lang: SupportedLanguage): void {
    this.languageService.setLanguage(lang);
  }

  get username(): FormControl {
    return this.loginForm.get('username') as FormControl;
  }

  get password(): FormControl {
    return this.loginForm.get('password') as FormControl;
  }

  login(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.showLoginError = false;
    this.loginError = '';
    this.isLoading = true;

    this.authService.login(this.username.value, this.password.value).subscribe(
      (response: any) => {
        this.logger.log('[Login] Login response received, storing token');
        localStorage.setItem('token', response.token);
        const decoded = this.authService.decodeToken() as any;
        this.logger.log('[Login] Decoded token after login:', { UserId: decoded?.UserId, UserCode: decoded?.UserCode, UserTypeId: decoded?.UserTypeId, exp: decoded?.exp });

        // Validate token before navigating
        if (!this.isTokenValid(decoded)) {
          this.logger.error('[Login] Token validation failed after successful login response');
          this.loginError = 'ໂທກັນບໍ່ຖືກຕ້ອງ | Token validation failed';
          this.showLoginError = true;
          this.isLoading = false;
          this.toastService.error(this.loginError, { duration: 5000, position: 'top-center' });
          this.authService.logout();
          return;
        }

        // JWT claims use PascalCase: UserId, UserTypeId, UserCode, Fullname
        const user = {
          user_id: decoded?.UserId,
          fullname: decoded?.Fullname,
          user_type: decoded?.UserTypeId,
        };

        // Initialize SignalR for real-time notifications
        // Use UserCode (AgentCode like A0061) for registration, not UserId
        if (decoded?.UserCode && decoded?.UserTypeId) {
          this.notificationService.initializeSignalR(decoded.UserCode, decoded.UserTypeId);
          this.logger.log('[Login] SignalR initialized for user:', decoded.UserCode, 'type:', decoded.UserTypeId);
        } else if (decoded?.UserId && decoded?.UserTypeId) {
          this.notificationService.initializeSignalR(decoded.UserId.toString(), decoded.UserTypeId);
          this.logger.warn('[Login] UserCode not found, using UserId:', decoded.UserId);
        }

        this.logger.log('[Login] Token valid, navigating to agent-hub');
        this.router.navigate(['/agent-hub']);
      },
      (error: any) => {
        this.isLoading = false;
        this.loginError = error?.error?.message || 'ເຂົ້າສູ່ລະບົບບໍ່ສຳເລັດ. ກະລຸນາກວດສອບຊື່ຜູ້ໃຊ້ ແລະ ລະຫັດຜ່ານຂອງທ່ານ.';
        this.showLoginError = true;

        // Also show toast for immediate feedback
        this.toastService.error(this.loginError, {
          duration: 5000,
          position: 'top-center'
        });
      }
    );
  }
}
